import React, { useEffect, useState } from "react";
import ReactDom from "react-dom";
import axios from "axios";
import { BookingEngine } from "../../hotel_integration_modules/index";
import translations from "../../hotel_integration_modules/utilities/translations";
import onContentChange from "../../hotel_integration_modules/utilities/onContentChange";
import GuestbookDark from "../UI/Icons/GuestbookDark";
import GuestbookLight from "../UI/Icons/GuestbookLight";
import GuestbookWide from "../UI/Icons/GuestbookWide";
import {
  numOfHotels,
  guestbookSites,
  environment,
  honeybadger,
} from "../../settings/IntegrationConstants";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import I18n from "../../translations/i18n";
import styled from "styled-components";
import { waitForSingleShadowSelectors } from "@/hotel_integration_modules/utilities/waitForSelectors";

type BackgroundPrimaryColorProp = {
  backgroundPrimaryColor: boolean;
};

type StyledEnrollmentContainerProp = {
  windowWidth: number;
  backgroundPrimaryColor: boolean;
  largeEnrollment: boolean;
  bookingEngineName: string;
  isActive: boolean;
};

const StyledEnrollmentContainer = styled.div<StyledEnrollmentContainerProp>`
  font-family: ${({ theme }) => theme.primaryFont};
  display: ${({ windowWidth, largeEnrollment }) =>
    windowWidth < 768 || !largeEnrollment ? "block" : "flex"} !important;
  justify-content: space-between !important;
  border: 1px solid #e4e4e4;
  padding: 16px 16px 8px 16px !important;
  position: relative !important;
  background: ${({ theme, backgroundPrimaryColor }) =>
    backgroundPrimaryColor ? theme.primaryColor : "#ffffff"} !important;
  color: ${({ backgroundPrimaryColor }) =>
    backgroundPrimaryColor ? "#ffffff" : "#343638"} !important;

  ${({ theme }) => theme.mobileL`
    display: block !important;
    margin-right: 0px !important;
   `}

  ${({ bookingEngineName, isActive }) =>
    bookingEngineName === 'bobw' && `
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      -khtml-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
      transition: border-color .15s, box-shadow .15s;

      ${isActive ? `
        border: 1px solid #16191d;
        box-shadow: 0 4px 28px #0000001a;
      ` : ''}

      &:hover {
        cursor: pointer;
        border: 1px solid #16191d;
        box-shadow: 0 4px 28px #0000001a;

        * {
          cursor: inherit; 
        }
      } 
    `
  }
`;

const SmallLogoContainer = styled.div`
  width: 62px  !important;
  display: flex  !important;
  flex-direction: column  !important;
  justify-content: flex-end  !important;
  margin-bottom: 8px  !important;
  margin-left: 16px  !important; 

  svg {
    height: 62px  !important;
    width: 62px  !important;
  }
`;

const WideLogoContainer = styled.div<BackgroundPrimaryColorProp>`
  width: fit-content !important;
  margin: 0 auto 8px !important;

  filter: ${({ backgroundPrimaryColor }) =>
    backgroundPrimaryColor ? "none" : "invert(1)"}  !important;
`;

const CheckboxContainer = styled.div<BackgroundPrimaryColorProp>`
  width: fit-content !important ;
  position: relative  !important;
  top: -10px  !important;

  .MuiTypography-root {
    font-family: inherit;
    font-size: 16px;
    color: ${({ backgroundPrimaryColor }) =>
    backgroundPrimaryColor ? "#ffffff" : "#343638"}  !important;

    ${({ theme }) => theme.mobileL`
      font-size: 14px;
   `}
  }

  .MuiSvgIcon-root {
    width: 30px !important;
    height: 30px !important;
  }

  .MuiCheckbox-root {
    padding: 6px !important;
    max-width: 42px !important;
    margin-right: 10px !important;
    color: #e4e4e4;

    &.MuiCheckbox-colorSecondary.Mui-checked {
      color: ${({ theme, backgroundPrimaryColor }) =>
    backgroundPrimaryColor ? "#ffffff" : theme.primaryColor} !important;

      &:hover {
        background-color: rgba(0, 0, 0, 0.04) !important;
      }
    }

    ${({ backgroundPrimaryColor }) =>
    backgroundPrimaryColor &&
    `
        color: #ffffff !important;

        &.MuiCheckbox-colorSecondary:after {
          left: 12px !important;
          top: 12px !important;
          height: 18px !important;
          width: 19px !important;
          position: absolute !important;
          z-index: 1;
        }

        &.MuiCheckbox-colorSecondary.Mui-checked:after {
          display: none !important;
        }
      `}

    ${({ theme }) => theme.mobileL`
      &.MuiCheckbox-colorSecondary:after {
        content: "" !important;
        left: 12px !important;
        top: 12px !important;
        height: 18px !important;
        width: 19px !important;
        position: absolute !important;
        z-index: 1;
      }

      &.MuiCheckbox-colorSecondary.Mui-checked:after {
        display: none !important;
      }
    `}
  }

  .MuiIconButton-root:hover {
    background-color: rgba(0, 0, 0, 0.04) !important;
  }
`;

const NonMemberMessageContainer = styled.div<BackgroundPrimaryColorProp>`
  position: relative !important;
  top: -8px !important;
  font-size: 16px ;
  font-weight: 400 !important;
  line-height: 24px !important;

  span {
    max-width: unset !important;
    width: fit-content !important;
    color: ${({ theme, backgroundPrimaryColor }) =>
    backgroundPrimaryColor ? "#ffffff!important" : theme.primaryColor} !important;
    font-weight: 500 !important;
  }

  .partner-hotels {
    color: ${({ theme, backgroundPrimaryColor }) =>
    backgroundPrimaryColor ? "#ffffff" : theme.primaryColor} !important;
  }

  a {
    text-decoration: underline !important;
    border-bottom: none !important;
    color: ${({ backgroundPrimaryColor }) =>
    backgroundPrimaryColor ? "#ffffff" : "inherit"} !important;
  }
`;

const MemberMessageContainer = styled.div<BackgroundPrimaryColorProp>`
  font-size: 16px;
  font-weight: 400 !important;
  line-height: 24px !important;
  margin-bottom: 8px !important;

  span {
    max-width: unset !important;
    width: fit-content !important;
    color: ${({ theme, backgroundPrimaryColor }) =>
    backgroundPrimaryColor ? "ffffff" : theme.primaryColor} !important;
    font-weight: 500 !important;
  }

  .partner-hotels {
    color: ${({ theme, backgroundPrimaryColor }) =>
    backgroundPrimaryColor ? "#ffffff" : theme.primaryColor} !important;

    ${({ theme }) => theme.mobileL`
      color: #ffffff!important;
   `}
  }

  a {
    text-decoration: underline !important;
    border-bottom: none !important;
    color: ${({ backgroundPrimaryColor }) =>
    backgroundPrimaryColor ? "#ffffff" : "inherit"} !important;

    ${({ theme }) => theme.mobileL`
      color: #ffffff!important;
   `}
  }
`;

const Terms = styled.a`
  font-size: 12px;
`;

type EnrollmentMessageProps = {
  showMessaging: boolean;
  bookingEngine: BookingEngine;
  existingMember: boolean;
  gbEnrollChecked: boolean;
  currency: string;
  locale: string;
  setExistingMember: React.Dispatch<React.SetStateAction<boolean>>;
  setGbEnrollChecked: React.Dispatch<React.SetStateAction<boolean>>;
  windowWidth: number;
  hotelSettings: any;
};

const EnrollmentMessage = ({
  showMessaging,
  bookingEngine,
  existingMember,
  gbEnrollChecked,
  currency,
  locale,
  setExistingMember,
  setGbEnrollChecked,
  windowWidth,
  hotelSettings
}: EnrollmentMessageProps) => {
  // Check to see if subtotal is captured and has valid amount, otherwise report to Honeybadger
  if (!bookingEngine.displaySubtotal()) {
    // honeybadger.notify("Invalid monetary amount on enrollment message", {
    //   tags: "new_bei, checkout_page",
    //   context: {
    //     bookingEngine: `${bookingEngine.name}`,
    //     property: `${bookingEngine.hotel.name}`,
    //   },
    // });
  }

  const enrollmentTarget = bookingEngine.getEnrollmentTarget();
  sessionStorage.removeItem("gbRewards15");
  sessionStorage.removeItem("gbRewards05");
  const [rewards15, setRewards15] = useState(bookingEngine.getRewards(0.15));
  const [rewards5, setRewards5] = useState(bookingEngine.getRewards(0.05));
  const whiteLabel = hotelSettings.hotel.whiteLabel;
  const cobrandingEnabled = hotelSettings.hotel.cobrandingEnabled;
  const rootNode = bookingEngine.shadowRoot || document;

  onContentChange(
    bookingEngine.selectors.paymentPage.contentChangeTarget,
    () => {
      sessionStorage.removeItem("gbRewards15");
      sessionStorage.removeItem("gbRewards05");
      setRewards15(bookingEngine.getRewards(0.15));
      setRewards5(bookingEngine.getRewards(0.05));
    },
    rootNode
  );

  const checkIfGuestbookMember = (email: string): void => {
    axios
      .get(
        `${guestbookSites[environment as string].rewardsdash.url
        }/api/v1/bei/member_info`,
        {
          params: {
            email: email,
          },
        }
      )
      .then((response) => {
        if (response.status === 200) {
          if (response.data.is_current_member) {
            // current member in the system
            setExistingMember(true);
            sessionStorage.setItem("isMemberOrEnrolled", "true");
          } else {
            // not a current member in the system
            setExistingMember(false);
            sessionStorage.setItem("isMemberOrEnrolled", "false");
          }
        } else {
          setExistingMember(false);
          sessionStorage.setItem("isMemberOrEnrolled", "false");
        }
      })
      .catch((error) => {
        // Check if Script Injector Extension in use
        if (bookingEngine.hotel.widgetIdentifier === "tradeshow") {
          if (email.includes("@theguestbook.com")) {
            setExistingMember(true);
            sessionStorage.setItem("isMemberOrEnrolled", "true");
          } else {
            setExistingMember(false);
            sessionStorage.setItem("isMemberOrEnrolled", "false");
          }
          // Handle network error otherwise
        } else {
          // unable to send request, network error
          console.log("Enrollment Network Error: ", error);

          honeybadger.notify(`Check if Guestbook member error: ${error}`, {
            tags: "new_bei, checkout_page, check_if_guestbook_member",
            context: {
              bookingEngine: `${bookingEngine.name}`,
              property: `${bookingEngine.hotel.name}`,
            },
          });

          // setGbEnrollChecked(false);
          setExistingMember(false);
          sessionStorage.setItem("isMemberOrEnrolled", "false");
          // sessionStorage.setItem("gbEnrolledChecked", "false");
        }
      });
  };

  useEffect(() => {
    const emailField = rootNode.querySelector(
      bookingEngine.selectors.paymentPage.email
    );
    if (bookingEngine.name == "selfbook") {
      let gbEmail = sessionStorage.getItem("gbEmail") ?
        sessionStorage.getItem("gbEmail") : "";
      checkIfGuestbookMember(gbEmail);
      emailField?.addEventListener("blur", () => {
        checkIfGuestbookMember(emailField.value.trim());
      });
    } else if (bookingEngine.name == "reztrip") {
      setTimeout(() => {
        checkIfGuestbookMember(
          emailField ? emailField.value.trim() :
            sessionStorage.getItem("gbBookingDetails") ? JSON.parse(sessionStorage.getItem("gbBookingDetails") as string)[0].email :
              ""
        );
        emailField?.addEventListener("blur", () => {
          checkIfGuestbookMember(emailField.value.trim());
        });
      }, 2000);
    } else if (bookingEngine.name == "pegs") {
      const POLL_INTERVAL = 500;
      const pollHandle = setInterval(() => {
        const emailField = document.querySelector(bookingEngine.selectors.paymentPage.email);
        const sessionData = sessionStorage.getItem("gbBookingDetails");

        let sessionEmail = "";
        if (sessionData) {
          const parsed = JSON.parse(sessionData);
          const rawEmail = parsed[0]?.email;
          if (rawEmail != null) sessionEmail = rawEmail.trim();
        }

        const fieldValue = emailField?.value.trim() || "";
        if (fieldValue || sessionEmail) {

          const emailToCheck = fieldValue || sessionEmail;
          checkIfGuestbookMember(emailToCheck);

          emailField?.addEventListener("blur", () => {
            const val = emailField.value.trim();
            if (val) checkIfGuestbookMember(val);
          });

          clearInterval(pollHandle);
        }
      }, POLL_INTERVAL);
    } else if (bookingEngine.name === "skipper") {
      const email = JSON.parse(document.querySelector('#skipper-ld-json').textContent).reservationDetails.underName.email
      checkIfGuestbookMember(email);

      emailField?.addEventListener("focusout", () => {
        const email = JSON.parse(document.querySelector('#skipper-ld-json').textContent).reservationDetails.underName.email
        checkIfGuestbookMember(email);
      });
    } else if (bookingEngine.name === 'travelclick_5') {
      waitForSingleShadowSelectors('amadeus-hos-res-wc-guest-details', bookingEngine.selectors.paymentPage.email, () => {
        const userFormRoot = document.querySelector('amadeus-hos-res-wc-guest-details').shadowRoot;
        const tc5EmailField = userFormRoot.querySelector(bookingEngine.selectors.paymentPage.email);

        checkIfGuestbookMember(
          tc5EmailField ? tc5EmailField.value.trim() :
            sessionStorage.getItem("gbBookingDetails") ? JSON.parse(sessionStorage.getItem("gbBookingDetails") as string)[0].email :
              ""
        );

        tc5EmailField?.addEventListener("blur", () => {
          checkIfGuestbookMember(tc5EmailField.value.trim());
        });
      });

    } else {
      // disable recheck member for siteminder
      if (bookingEngine.name === 'siteminder' && sessionStorage.getItem('isMemberOrEnrolled') === 'true') {
        return;
      }

      checkIfGuestbookMember(
        emailField ? emailField.value.trim() :
          sessionStorage.getItem("gbBookingDetails") ? JSON.parse(sessionStorage.getItem("gbBookingDetails") as string)[0].email :
            ""
      );
      emailField?.addEventListener("blur", () => {
        checkIfGuestbookMember(emailField.value.trim());
      });
    }
  }, []);
  const earnMessage = (
    <>
      <span>
        {currency +
          I18n.t(
            `views.hotel_integration.components.enrollment_messaging.cash_back`,
            {
              locale: `${locale}`,
              five_rewards: rewards5,
            }
          )}
      </span>
      {", "}
      <span>
        {currency +
          I18n.t(
            `views.hotel_integration.components.enrollment_messaging.cash_for_a_cause`,
            {
              locale: `${locale}`,
              five_rewards: rewards5,
            }
          )}
      </span>
      , {`${translations("enrollment_messaging", "or", locale)} `}
      <span>
        {currency +
          I18n.t(
            `views.hotel_integration.components.enrollment_messaging.cash_forward`,
            {
              locale: `${locale}`,
              fifteen_rewards: rewards15,
            }
          )}
      </span>{" "}
      {`${translations(
        "enrollment_messaging",
        "towards_a_future_stay",
        locale
      )} `}
      <a
        className="partner-hotels"
        href={`https://theguestbook.com/destinations/guestbook`}
        target="_blank"
        rel="noreferrer"
        style={{ wordBreak: "initial" }}
      >
        {I18n.t(
          `views.hotel_integration.components.enrollment_messaging.partner_hotels`,
          {
            locale: `${locale}`,
            num_of_hotels: numOfHotels,
          }
        )}
      </a>
      .&nbsp;
      <Terms
        className="terms"
        href="https://theguestbook.com/terms"
        target="_blank"
        rel="noreferrer"
      >{`${translations(
        "enrollment_messaging",
        "terms_of_use",
        locale
      )}`}</Terms>
    </>
  );

  if (
    sessionStorage.getItem("rateExcluded") !== "true" &&
    (showMessaging || existingMember) &&
    enrollmentTarget
  ) {
    return ReactDom.createPortal(
      <StyledEnrollmentContainer
        bookingEngineName={bookingEngine.name}
        isActive={gbEnrollChecked || existingMember}
        windowWidth={windowWidth}
        backgroundPrimaryColor={
          bookingEngine.hotel.desktopEnrollmentPrimaryColor
        }
        largeEnrollment={bookingEngine.largeEnrollmentMessage}
        className="gb-enrollment-container"
        onClick={(e) => {
          e.stopPropagation();
          if (bookingEngine.name === 'bobw' && !existingMember) {
            setGbEnrollChecked(!gbEnrollChecked);
            sessionStorage.setItem(
              "gbEnrolledChecked",
              (!gbEnrollChecked).toString()
            );
            sessionStorage.setItem(
              "isMemberOrEnrolled",
              (!gbEnrollChecked).toString()
            );
          }
        }}
      >
        {(windowWidth <= 767 || !bookingEngine.largeEnrollmentMessage) && (
          <WideLogoContainer
            backgroundPrimaryColor={bookingEngine.hotel.desktopEnrollmentPrimaryColor}
            className="wide-logo-container"
          >
            <GuestbookWide />
          </WideLogoContainer>
        )}
        <div>
          {existingMember || sessionStorage.getItem('isMemberOrEnrolled') === 'true' ? (
            <MemberMessageContainer
              backgroundPrimaryColor={
                bookingEngine.hotel.desktopEnrollmentPrimaryColor
              }
              className="memberMessageContainer"
            >
              {
                (cobrandingEnabled) ?
                  I18n.t(`views.hotel_integration.components.enrollment_messaging.thanks_cobrand`, {
                    locale: `${locale}`,
                    white_label: `${whiteLabel}`
                  })
                  :
                  translations("enrollment_messaging", "thanks", locale)
              }
              {": "}
              {earnMessage}
            </MemberMessageContainer>
          ) : (
            <>
              <CheckboxContainer
                backgroundPrimaryColor={
                  bookingEngine.hotel.desktopEnrollmentPrimaryColor
                }
                className="checkbox-container"
                onClick={() => {
                  if (bookingEngine.name == 'reztrip') {
                    setGbEnrollChecked(!gbEnrollChecked);
                    sessionStorage.setItem(
                      "gbEnrolledChecked",
                      (!gbEnrollChecked).toString()
                    );
                    sessionStorage.setItem(
                      "isMemberOrEnrolled",
                      (!gbEnrollChecked).toString()
                    );
                  }
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        sessionStorage.getItem("gbEnrolledChecked") === 'true'
                          ? true : gbEnrollChecked}
                      onChange={(e) => {
                        e.stopPropagation();
                        if (bookingEngine.name === 'windsurfercrs') {
                          setGbEnrollChecked(!gbEnrollChecked);
                          localStorage.setItem(
                            "gbEnrolledChecked",
                            (!gbEnrollChecked).toString()
                          );
                          localStorage.setItem(
                            "isMemberOrEnrolled",
                            (!gbEnrollChecked).toString()
                          );
                        } else {
                          setGbEnrollChecked(!gbEnrollChecked);
                          sessionStorage.setItem(
                            "gbEnrolledChecked",
                            (!gbEnrollChecked).toString()
                          );
                          sessionStorage.setItem(
                            "isMemberOrEnrolled",
                            (!gbEnrollChecked).toString()
                          );
                        }
                      }}
                      name="guestbook-enroll"
                      className="checkbox"
                      disabled={bookingEngine.name === 'bobw'}
                    />
                  }
                  label={
                    cobrandingEnabled ?
                      I18n.t(`views.hotel_integration.components.enrollment_messaging.join_cobrand`, {
                        locale: `${locale}`,
                        white_label: `${whiteLabel}`
                      })
                      :
                      translations("enrollment_messaging", "join", locale)
                  }
                  className="gb-form-control-label"
                />
              </CheckboxContainer>
              <NonMemberMessageContainer
                backgroundPrimaryColor={
                  bookingEngine.hotel.desktopEnrollmentPrimaryColor
                }
                className="nonMemberMessageContainer"
              >
                {`${translations(
                  "enrollment_messaging",
                  "you_earn",
                  locale
                )}: `}{" "}
                {earnMessage}
              </NonMemberMessageContainer>
            </>
          )}
        </div>
        {windowWidth > 767 && bookingEngine.largeEnrollmentMessage && (
          <SmallLogoContainer className="smallLogoContainer">
            {bookingEngine.hotel.desktopEnrollmentPrimaryColor ? (
              <GuestbookLight />
            ) : (
              <GuestbookDark />
            )}
          </SmallLogoContainer>
        )}
      </StyledEnrollmentContainer>,
      enrollmentTarget as Element
    );
  } else {
    return null;
  }
};

export default EnrollmentMessage;
